// Gauge cluster — momentum + RVOL + MA divergence (gauges-handoff.md).
// Pure math + the approved SVG speedometer renderer from the chat prototype.
// Same formulas as pine/gauges.pine — the two surfaces must always agree.
// All three gauges compute on the SELECTED gauge timeframe (gaugeTf in charts.ts:
// 5m/1H/D/W), matching pine/gauges.pine. The cluster fetches its own Alpaca bars —
// Charts+Trading embeds a sealed TradingView iframe, so charts.ts owns that fetch.

export interface Bar { t: string; o: number; h: number; l: number; c: number; v: number; }

// Settings synced from KV → localStorage at boot (auth.ts); change from chat
// via update_setting, then reload.
const setting = (key: string, dflt: number): number => {
  const v = parseFloat(localStorage.getItem(key) ?? '');
  return Number.isFinite(v) ? v : dflt;
};

const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));

// ── Math (bars oldest-first) ──────────────────────────────────────────────────

function rsi14(closes: number[]): number {
  const period = 14;
  let gains = 0, losses = 0;
  for (let i = closes.length - period; i < closes.length; i++) {
    const d = closes[i] - closes[i - 1];
    if (d > 0) gains += d; else losses -= d;
  }
  if (losses === 0) return 100;
  return 100 - 100 / (1 + gains / losses);
}

export interface MomentumResult { score: number; rsi: number; smaDist: number; r5: number; climax: boolean; }

export function momentumScore(bars: Bar[]): MomentumResult | null {
  if (bars.length < 20) return null;
  const closes = bars.map(b => b.c);
  const last = closes[closes.length - 1];

  const rsi = rsi14(closes);                                          // 0–100 already
  const sma20 = closes.slice(-20).reduce((a, b) => a + b, 0) / 20;
  const smaDist = ((last - sma20) / sma20) * 100;                     // %
  const smaScore = (clamp(smaDist, -20, 20) + 20) / 40 * 100;         // ±20% → 0–100
  const r5 = ((last - closes[closes.length - 6]) / closes[closes.length - 6]) * 100;
  const r5Score = (clamp(r5, -15, 15) + 15) / 30 * 100;               // ±15% → 0–100

  const score = Math.round(0.40 * rsi + 0.35 * smaScore + 0.25 * r5Score);

  // Climax: max momentum on a volume blowoff = distribution warning (Pivot Matrix #2)
  const vols = bars.map(b => b.v);
  const avgVol20 = vols.slice(-21, -1).reduce((a, b) => a + b, 0) / 20;
  const climax = score >= setting('momentum_climax_score', 80)
    && r5 >= setting('momentum_climax_r5', 12)
    && vols[vols.length - 1] >= 2 * avgVol20;

  return { score, rsi: Math.round(rsi), smaDist, r5, climax };
}

export function rvol(bars: Bar[]): { x: number; lastVol: number; avgVol: number } | null {
  const lookback = setting('gauge_rvol_lookback', 20);
  if (bars.length < lookback + 1) return null;
  const vols = bars.map(b => b.v);
  const lastVol = vols[vols.length - 1];
  const prior = vols.slice(-(lookback + 1), -1);                      // exclude last bar
  const avgVol = prior.reduce((a, b) => a + b, 0) / prior.length;
  return avgVol > 0 ? { x: lastVol / avgVol, lastVol, avgVol } : null;
}

export function maDivergence(bars: Bar[]): { div: number; n: number } | null {
  const n = Math.round(setting('gauge_div_ma', 30));
  if (bars.length < n + 1) return null;
  const closes = bars.map(b => b.c);
  const sma = closes.slice(-n).reduce((a, b) => a + b, 0) / n;
  return { div: ((closes[closes.length - 1] - sma) / sma) * 100, n };
}

// ── SVG renderer (chat-prototype geometry, verbatim) ──────────────────────────

const A0 = Math.PI * 0.85, A1 = Math.PI * 2.15, SPAN = A1 - A0;

function arc(cx: number, cy: number, r: number, a0: number, a1: number): string {
  const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
  const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
  const large = (a1 - a0) > Math.PI ? 1 : 0;
  return `M${x0.toFixed(1)} ${y0.toFixed(1)} A${r} ${r} 0 ${large} 1 ${x1.toFixed(1)} ${y1.toFixed(1)}`;
}

interface Zone { to: number; c: string; }

function zonesArc(zones: Zone[]): string {
  let p = '', from = 0;
  for (const z of zones) {
    p += `<path d="${arc(80, 80, 58, A0 + SPAN * from, A0 + SPAN * z.to)}" stroke="${z.c}" stroke-width="11" fill="none" opacity="0.85"/>`;
    from = z.to;
  }
  return p;
}

function needle(frac: number): string {
  const a = A0 + SPAN * clamp(frac, 0, 1);
  const nx = 80 + 44 * Math.cos(a), ny = 80 + 44 * Math.sin(a);
  return `<line x1="80" y1="80" x2="${nx.toFixed(1)}" y2="${ny.toFixed(1)}" stroke="#888780" stroke-width="3" stroke-linecap="round"/><circle cx="80" cy="80" r="5" fill="#888780"/>`;
}

export interface GaugeSpec {
  title: string;
  zones: Zone[];
  frac: number | null;     // null = warming up, no needle
  value: string;           // big value text
  valueColor: string;
  sub: string;             // substat line
  badge?: string;          // e.g. "CLIMAX"
  zeroTick?: boolean;      // divergence: 0 tick at top center
}

export function gaugeSvg(g: GaugeSpec): string {
  const tick = g.zeroTick
    ? `<line x1="80" y1="14" x2="80" y2="22" stroke="#888780" stroke-width="2"/>`
    : '';
  return `
  <div style="text-align:center;min-width:160px;">
    <svg viewBox="0 0 160 112" style="width:160px;height:112px;">
      ${zonesArc(g.zones)}
      ${tick}
      ${g.frac == null ? '' : needle(g.frac)}
      <text x="80" y="104" text-anchor="middle" fill="${g.valueColor}" font-family="var(--mono)" font-size="17" font-weight="700">${g.value}</text>
    </svg>
    <div style="font-size:10px;color:var(--text3);letter-spacing:0.1em;text-transform:uppercase;">${g.title}</div>
    <div style="font-size:10px;color:var(--text3);margin-top:1px;">${g.sub}</div>
    ${g.badge ? `<div style="display:inline-block;margin-top:3px;font-size:9px;font-weight:700;letter-spacing:0.12em;color:#e0b040;border:1px solid #e0b04066;border-radius:3px;padding:1px 6px;">${g.badge}</div>` : ''}
  </div>`;
}

// ── Cluster assembly ──────────────────────────────────────────────────────────

const TEAL = '#26a69a', AMBER = '#e0b040', RED = '#ef5350', GRAY = '#3a4350';
const WARM = (title: string, n: number, need: number): GaugeSpec => ({
  title, zones: [{ to: 1, c: GRAY }], frac: null, value: '—',
  valueColor: 'var(--text3)', sub: `warming up · ${n}/${need} bars`,
});

export function buildGauges(bars: Bar[], tfLabel: string): string {
  const specs: GaugeSpec[] = [];

  // 1. Momentum (selected gauge timeframe)
  const m = momentumScore(bars);
  if (!m) specs.push(WARM('Momentum', bars.length, 20));
  else specs.push({
    title: `Momentum · ${tfLabel}`,
    zones: [{ to: 0.35, c: RED }, { to: 0.65, c: AMBER }, { to: 1, c: TEAL }],
    frac: m.score / 100,
    value: String(m.score),
    valueColor: m.score >= 65 ? TEAL : m.score >= 35 ? AMBER : RED,
    sub: `RSI ${m.rsi} · SMA20 ${m.smaDist >= 0 ? '+' : ''}${m.smaDist.toFixed(1)}% · 5b ${m.r5 >= 0 ? '+' : ''}${m.r5.toFixed(1)}%`,
    badge: m.climax ? 'CLIMAX' : undefined,
  });

  // 2. RVOL (selected gauge timeframe)
  const r = rvol(bars);
  if (!r) specs.push(WARM('RVOL', bars.length, Math.round(setting('gauge_rvol_lookback', 20)) + 1));
  else specs.push({
    title: `RVOL · ${tfLabel}`,
    zones: [{ to: 0.2, c: GRAY }, { to: 0.4, c: AMBER }, { to: 1, c: TEAL }],   // 0–1x / 1–2x / >2x on a 0–5x dial
    frac: Math.min(r.x / 5, 1),
    value: r.x.toFixed(1) + 'x',
    valueColor: r.x >= 2 ? TEAL : r.x >= 1 ? AMBER : 'var(--text3)',
    sub: `${(r.lastVol / 1e6).toFixed(2)}M vs ${(r.avgVol / 1e6).toFixed(2)}M avg`,
  });

  // 3. MA divergence (selected gauge timeframe)
  const d = maDivergence(bars);
  const range = setting('gauge_div_range', 500);
  if (!d) specs.push(WARM(`${Math.round(setting('gauge_div_ma', 30))}MA Div · ${tfLabel}`, bars.length, Math.round(setting('gauge_div_ma', 30)) + 1));
  else specs.push({
    title: `${d.n}MA Div · ${tfLabel}`,
    zones: [{ to: 0.2, c: '#a83232' }, { to: 0.42, c: '#d4756b' }, { to: 0.58, c: GRAY }, { to: 0.8, c: '#5dbbaf' }, { to: 1, c: TEAL }],
    frac: (clamp(d.div, -range, range) + range) / (2 * range),
    value: `${d.div >= 0 ? '+' : ''}${d.div.toFixed(1)}%`,
    valueColor: d.div >= 0 ? TEAL : RED,
    sub: `vs ${d.n}-bar SMA · dial ±${range}%`,
    zeroTick: true,
  });

  return specs.map(gaugeSvg).join('');
}
